import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthProvider'
import { useMobileDocuments } from '@/hooks/useMobileDocuments'
import { Badge } from '@/components/ui/badge'
import { Loader2, ArrowLeft, FileText, FileCheck, HeartPulse, AlertTriangle, Star, Download, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'

const typeConfig = {
  contract: { label: 'Contract', icon: FileCheck, color: 'text-blue-600 bg-blue-50', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  sickness: { label: 'Sickness', icon: HeartPulse, color: 'text-red-500 bg-red-50', badge: 'bg-red-50 text-red-700 border-red-200' },
  disciplinary: { label: 'Disciplinary', icon: AlertTriangle, color: 'text-amber-500 bg-amber-50', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  performance: { label: 'Performance', icon: Star, color: 'text-purple-500 bg-purple-50', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  other: { label: 'Other', icon: FileText, color: 'text-gray-500 bg-gray-50', badge: 'bg-gray-50 text-gray-600 border-gray-200' },
}

function formatFileSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

export default function DocumentsPage() {
  const navigate = useNavigate()
  const { userProfile } = useAuth()
  const { documents, loading } = useMobileDocuments(userProfile?.id)
  const [filter, setFilter] = useState('all')

  const filteredDocs = filter === 'all'
    ? documents
    : documents.filter(d => d.document_type === filter)

  const typeCounts = documents.reduce((acc, d) => {
    acc[d.document_type] = (acc[d.document_type] || 0) + 1
    return acc
  }, {})

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/more')} className="p-1 -ml-1 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">Documents</h1>
      </div>

      {documents.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No documents found.</p>
          <p className="text-xs text-gray-400 mt-1">Documents added to your profile will appear here.</p>
        </div>
      ) : (
        <>
          {/* Filter chips */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4 no-scrollbar">
            <button
              onClick={() => setFilter('all')}
              className={cn(
                'shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                filter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
              )}
            >
              All ({documents.length})
            </button>
            {Object.entries(typeConfig).map(([key, cfg]) => {
              const count = typeCounts[key]
              if (!count) return null
              return (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={cn(
                    'shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                    filter === key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
                  )}
                >
                  {cfg.label} ({count})
                </button>
              )
            })}
          </div>

          {/* Document list */}
          <div className="space-y-2">
            {filteredDocs.map(doc => {
              const cfg = typeConfig[doc.document_type] || typeConfig.other
              const Icon = cfg.icon

              return (
                <div key={doc.id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex gap-3">
                    <div className={cn('shrink-0 w-10 h-10 rounded-lg flex items-center justify-center', cfg.color)}>
                      <Icon className="h-4.5 w-4.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{doc.title}</p>
                          <p className="text-xs text-gray-400 truncate mt-0.5">{doc.file_name}</p>
                        </div>
                        <Badge variant="outline" className={cn('text-[10px] capitalize shrink-0', cfg.badge)}>
                          {cfg.label}
                        </Badge>
                      </div>

                      {doc.description && (
                        <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{doc.description}</p>
                      )}

                      <div className="flex items-center gap-3 mt-2">
                        {doc.file_size && (
                          <span className="text-[10px] text-gray-400">{formatFileSize(doc.file_size)}</span>
                        )}
                        {doc.effective_date && (
                          <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                            <Calendar className="h-2.5 w-2.5" />
                            {new Date(doc.effective_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                        {doc.expiry_date && (
                          <span className={cn(
                            'text-[10px] flex items-center gap-0.5',
                            new Date(doc.expiry_date) < new Date() ? 'text-red-500' : 'text-gray-400'
                          )}>
                            Expires {new Date(doc.expiry_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                      </div>

                      {doc.file_url && (
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-2 text-xs text-emerald-600 font-medium hover:text-emerald-700 transition-colors"
                        >
                          <Download className="h-3 w-3" />
                          View / Download
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
